import { Component, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ModalV2Component } from "../../../../../shared/modal-v2/modal-v2.component";
import { EDiaryService } from "../../../../../core/services/e-diary.service";
import { AlertService } from "../../../../../shared/alert/alert.service";
import { MfaService } from "../../../../../core/services/mfa.service";
import { EdiaryEntryDetails } from "../../../../../core/models/trial-diary-form/ediary-entry-details.model";
import { LogHelper } from "../../../../../core/helpers/log.helper";
import {
  AdminEdiaryEntryForm,
  AdminEdiaryEntryQuestion,
  AdminEdiaryEntryQuestionOption
} from "../../../../../core/models/trial-diary-form/admin-ediary-form.model";
import { SelectOption } from 'app/core/models/select-option.model';
import { Subscription } from "rxjs";

@Component({
  selector: 'app-patient-ediary-entry-modal',
  templateUrl: './patient-ediary-entry-modal.component.html',
  styleUrl: './patient-ediary-entry-modal.component.scss'
})
export class PatientEdiaryEntryModalComponent implements OnDestroy {
  @Input() patientTrialId: string;
  @Output() saved = new EventEmitter<void>();
  @ViewChild('modal') modal: ModalV2Component;

  private formSub?: Subscription;

  protected isBusy: boolean = false;
  protected isSaving: boolean = false;
  protected entry: EdiaryEntryDetails;
  protected editForm: AdminEdiaryEntryForm;
  protected editable = false;
  protected form: FormGroup = new FormGroup({});
  protected formReady = false;
  protected dropdownOptionsMap: Record<string, SelectOption[]> = {};

  constructor(
    private ediaryService: EDiaryService,
    private alertService: AlertService,
    private mfaService: MfaService,
    private fb: FormBuilder) {}

  ngOnDestroy(): void {
    this.formSub?.unsubscribe();
  }

  showEntry(entryId: string) {
    this.editable = false;
    this.isBusy = true;
    this.resetForm();
    this.modal.show();

    this.ediaryService.getEdiaryEntryDetails(entryId).subscribe({
      next: entry => {
        this.entry = entry;
        this.isBusy = false;
      },
      error: err => {
        LogHelper.log(err);
        this.alertService.showErrorAlert(err);
        this.isBusy = false;
      }
    });
  }

  showEdit(entryId: string) {
    if (!this.patientTrialId) {
      this.alertService.showWarningAlert('Missing patient trial id');
      return;
    }

    this.editable = true;
    this.isBusy = true;
    this.resetForm();
    this.modal.show();

    this.ediaryService.getEdiaryEntryForEdit(entryId)
      .subscribe({
        next: form => {
          this.editForm = form;
          this.dropdownOptionsMap = this.buildDropdownOptions(form.questions);
          this.form = this.buildForm(form.questions);
          this.syncFormToQuestions(form.questions);
          this.formReady = true;
          this.isBusy = false;
        },
        error: err => {
          LogHelper.log(err);
          this.alertService.showErrorAlert(err);
          this.isBusy = false;
          this.editable = false;
        }
      });
  }

  private resetForm(): void {
    this.formSub?.unsubscribe();
    this.form = new FormGroup({});
    this.formReady = false;
  }

  onSingleAnswerChange(question: AdminEdiaryEntryQuestion, answer: string | null | undefined) {
    const value = answer ?? '';
    question.answers = value === '' ? [] : [value];
    this.form?.get(question.questionId)?.setValue(value);
  }

  protected dropdownOptions(questionId: string): SelectOption[] {
    return this.dropdownOptionsMap[questionId] ?? [];
  }

  private buildForm(questions: AdminEdiaryEntryQuestion[]): FormGroup {
    const controls: Record<string, FormControl | FormArray> = {};
    questions.forEach(question => {
      if (question.type === 'Checkbox') {
        const options = question.options || [];
        const answers = question.answers || [];
        const looksLikeLegacyBooleanArray =
          answers.length === options.length &&
          answers.every(a => this.isBooleanLike(a));

        const normalizedAnswers = looksLikeLegacyBooleanArray
          ? []
          : (answers || []).map(a => this.normalizeAnswer(a)).filter(a => a !== '');

        const initialChecks = options.map((opt, idx) => {
          if (looksLikeLegacyBooleanArray) {
            return this.isTruthy(answers[idx]);
          }

          return this.answerMatchesOption(normalizedAnswers, opt);
        });

        // Keep answers in the new format (selected option ids) so required validation works.
        question.answers = options
          .filter((_, idx) => initialChecks[idx])
          .map(opt => opt?.id || opt?.text)
          .filter(a => !!a);

        controls[question.questionId] = new FormArray(
          initialChecks.map(isChecked => new FormControl(isChecked))
        );
      } else {
        const initialValue = question.answers && question.answers.length > 0 ? question.answers[0] : '';
        controls[question.questionId] = new FormControl(initialValue);
      }
    });
    return this.fb.group(controls);
  }

  private syncFormToQuestions(questions: AdminEdiaryEntryQuestion[]): void {
    this.formSub?.unsubscribe();

    const questionMap = questions.reduce((acc, q) => acc.set(q.questionId, q), new Map<string, AdminEdiaryEntryQuestion>());

    this.formSub = this.form.valueChanges.subscribe(values => {
      Object.keys(values).forEach(key => {
        const question = questionMap.get(key);
        if (!question) return;
        const val = values[key];

        if (question.type === 'Checkbox' && Array.isArray(val) && question.options) {
          const selectedIds = (val as boolean[])
            .map((isChecked, idx) => (isChecked ? question.options[idx]?.id : null))
            .filter(id => id !== null) as string[];
          question.answers = selectedIds;
        } else if (Array.isArray(val)) {
          question.answers = val;
        } else {
          question.answers = val !== null && val !== undefined && val !== '' ? [val] : [];
        }
      });
    });
  }

  private buildDropdownOptions(questions: AdminEdiaryEntryQuestion[]): Record<string, SelectOption[]> {
    return questions.reduce((acc, question) => {
      acc[question.questionId] = (question.options || []).map(option => ({
        value: option.id,
        text: option.text
      }) as SelectOption);
      return acc;
    }, {} as Record<string, SelectOption[]>);
  }

  private normalizeAnswer(value: string | boolean | null | undefined): string {
    return (value ?? '').toString().trim().toLowerCase();
  }

  private isBooleanLike(value: string | boolean | null | undefined): boolean {
    const normalized = this.normalizeAnswer(value);
    return normalized === 'true' || normalized === 'false';
  }

  private isTruthy(value: string | boolean | null | undefined): boolean {
    return this.normalizeAnswer(value) === 'true';
  }

  private answerMatchesOption(normalizedAnswers: string[], option: AdminEdiaryEntryQuestionOption): boolean {
    const idMatch = normalizedAnswers.includes(this.normalizeAnswer(option?.id));
    const textMatch = normalizedAnswers.includes(this.normalizeAnswer(option?.text));
    return idMatch || textMatch;
  }

  isSaveDisabled(): boolean {
    if (!this.editForm || this.isSaving) {
      return true;
    }

    const requiredUnanswered = this.editForm.questions.some(q => q.isRequired && (!q.answers || q.answers.length === 0 || q.answers[0] === ''));
    return requiredUnanswered;
  }

  onSave() {
    if (!this.editForm || this.isSaveDisabled()) {
      return;
    }

    this.isSaving = true;

    this.mfaService.authenticateWithMfa().subscribe({
      next: (success) => {
        if (!success) {
          this.isSaving = false;
          this.alertService.showWarningAlert('User authentication failed. Changes cannot be saved');
          return;
        }

        this.saveEntry();
      },
      error: (err) => {
        LogHelper.log(err);
        this.isSaving = false;
        this.alertService.showWarningAlert('User authentication failed. Changes cannot be saved');
      }
    });
  }

  private saveEntry() {
    const payload = {
      entryId: this.editForm.entryId,
      diaryFormId: this.editForm.diaryFormId,
      entryDate: this.editForm.entryDate,
      questions: this.editForm.questions.map(q => {
        const controlValue = this.form.get(q.questionId)?.value;
        const answers = Array.isArray(controlValue)
          ? (controlValue as boolean[])
            .map((checked, idx) => checked ? (q.options?.[idx]?.id ?? q.options?.[idx]?.text) : null)
            .filter((v): v is string => !!v)
          : (controlValue !== null && controlValue !== undefined && controlValue !== '' ? [controlValue] : []);
        return {
          questionId: q.questionId,
          answers: answers.filter(a => a !== null && a !== undefined)
        };
      })
    };

    this.ediaryService.upsertEdiaryEntry(this.patientTrialId, payload).subscribe({
      next: () => {
        this.isSaving = false;
        this.alertService.showSuccessAlert('E-diary entry saved');
        this.modal.hide();
        this.editForm = null;
        this.editable = false;
        this.entry = null;
        this.saved.emit();
      },
      error: err => {
        LogHelper.log(err);
        this.alertService.showErrorAlert(err);
        this.isSaving = false;
      }
    });
  }
}
